import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Textarea } from '@/common/ui/textarea'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { ChannelSelector } from '../../components/ChannelSelector'
import { SMSSettingsForm } from '../../components/SMSSettingsForm'
import { LINESettingsForm } from '../../components/LINESettingsForm'
import { FlexMessageSelector } from '../../components/FlexMessageSelector'
import useQueryGetAutoMessageConditions from '../../api/hooks/useQueryGetAutoMessageConditions'
import useMutationCreateAutoMessage from '../../api/hooks/useMutationCreateAutoMessage'
import {
	MessageChannel,
	MessageTransactionType,
	MessageConditionType,
	MessageCondition,
} from '../../types'
import { getConditionLabel } from '../../utils/conditionLabels'
import { calculateCredit, getLanguageLabel } from '../../utils/smsCredits'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

import type { CreditData } from '../../utils/smsCredits'

const autoMessageFormSchema = z
	.object({
		message: z.string().optional(),
		condition: z.string().min(1, 'กรุณาเลือกเงื่อนไข'),
		minimum: z.number().optional(),
		maximum: z.number().optional(),
		channels: z
			.array(z.nativeEnum(MessageChannel))
			.min(1, 'กรุณาเลือกอย่างน้อย 1 ช่องทาง'),
		providerId: z.string().optional(),
		senderId: z.string().optional(),
		hotGameFlexMessageId: z.string().optional(),
	})
	.refine(
		(data) => {
			// Message is required when not using LINE_HOT_GAME
			if (!data.channels.includes(MessageChannel.LINE_HOT_GAME)) {
				return !!data.message && data.message.trim().length > 0
			}
			return true
		},
		{
			message: 'กรุณากรอกข้อความ',
			path: ['message'],
		},
	)
	.refine(
		(data) => {
			// Validate message length if provided
			if (data.message && data.message.length > 1000) {
				return false
			}
			return true
		},
		{
			message: 'ข้อความยาวเกินไป (สูงสุด 1000 ตัวอักษร)',
			path: ['message'],
		},
	)
	.refine(
		(data) => {
			// If SMS is selected, require provider and sender
			if (data.channels.includes(MessageChannel.SMS)) {
				return !!data.providerId && !!data.senderId
			}
			return true
		},
		{
			message: 'กรุณาเลือกผู้ให้บริการและชื่อผู้ส่ง SMS',
			path: ['providerId'],
		},
	)
	.refine(
		(data) => {
			// If LINE_HOT_GAME is selected, require hotGameFlexMessageId
			if (data.channels.includes(MessageChannel.LINE_HOT_GAME)) {
				return !!data.hotGameFlexMessageId
			}
			return true
		},
		{
			message: 'กรุณาเลือก Flex Message',
			path: ['hotGameFlexMessageId'],
		},
	)

type AutoMessageFormValues = z.infer<typeof autoMessageFormSchema>

const CreateAutoMessagePage = () => {
	const navigate = useNavigate()
	const { data: conditionsData, isLoading: isLoadingConditions } =
		useQueryGetAutoMessageConditions()
	const createAutoMessageMutation = useMutationCreateAutoMessage()

	// Filter out BALANCE_DEPLETED_NO_DEPOSIT as it has its own separate Broke Flex Message pages
	const availableConditions = conditionsData?.conditions?.filter(
		(c) => c.name !== 'BALANCE_DEPLETED_NO_DEPOSIT',
	)

	const form = useForm<AutoMessageFormValues>({
		resolver: zodResolver(autoMessageFormSchema),
		defaultValues: {
			message: '',
			condition: '',
			channels: [],
			providerId: '',
			senderId: '',
			hotGameFlexMessageId: '',
		},
	})

	const selectedCondition = form.watch('condition')
	const selectedChannels = form.watch('channels')

	const currentCondition = availableConditions?.find(
		(c) => c.name === selectedCondition,
	)

	const onSubmit = async (values: AutoMessageFormValues) => {
		try {
			await createAutoMessageMutation.mutateAsync({
				senderId: values.senderId || '',
				message: values.message || '',
				playerCondition: {
					// Cast to MessageCondition as backend accepts the combined enum
					condition: values.condition as MessageCondition,
					minimum: values.minimum,
					maximum: values.maximum,
				},
				scheduleDate: new Date().toISOString(),
				type: MessageTransactionType.AUTO,
				channels: values.channels,
				hotGameFlexMessageId: values.hotGameFlexMessageId || undefined,
				enableInstantClaim: false,
			})

			toast.success('สร้างข้อความอัตโนมัติสำเร็จ')
			navigate('/operator/messaging/auto')
		} catch {
			toast.error('เกิดข้อผิดพลาดในการสร้างข้อความอัตโนมัติ')
		}
	}

	const message = form.watch('message') || ''

	// Calculate SMS credits if SMS channel is selected
	let creditData: CreditData | null = null
	try {
		if (selectedChannels.includes(MessageChannel.SMS) && message) {
			creditData = calculateCredit(message)
		}
	} catch (error) {
		// Handle credit calculation errors (e.g., exceeds max credits)
		console.error('Credit calculation error:', error)
	}

	return (
		<PageContainer>
			<div className="space-y-6">
				<div>
					<h1 className="text-3xl font-bold">สร้างข้อความอัตโนมัติ</h1>
					<p className="mt-2 text-muted-foreground">
						สร้างข้อความที่จะถูกส่งอัตโนมัติตามเงื่อนไข
					</p>
				</div>

				<Card>
					<CardHeader>
						<CardTitle>ตั้งค่าการส่งข้อความอัตโนมัติ</CardTitle>
					</CardHeader>
					<CardContent>
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit, showFormValidationToast)}
								className="space-y-6"
							>
								{/* Condition */}
								<FormField
									control={form.control}
									name="condition"
									render={({ field }) => (
										<FormItem>
											<FormLabel>เงื่อนไขอัตโนมัติ</FormLabel>
											<FormControl>
												<Select
													onValueChange={field.onChange}
													value={field.value}
													disabled={isLoadingConditions}
												>
													<SelectTrigger>
														<SelectValue placeholder="เลือกเงื่อนไข" />
													</SelectTrigger>
													<SelectContent>
														{availableConditions?.map((condition) => (
															<SelectItem
																key={condition.name}
																value={condition.name}
															>
																{getConditionLabel(condition.name)}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</FormControl>
											<FormDescription>
												เลือกเงื่อนไขที่จะทริกเกอร์การส่งข้อความอัตโนมัติ
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								{/* Condition values */}
								{currentCondition &&
									currentCondition.type === MessageConditionType.SINGLE && (
										<FormField
											control={form.control}
											name="minimum"
											render={({ field }) => (
												<FormItem>
													<FormLabel>ค่าที่ต้องการ</FormLabel>
													<FormControl>
														<Input
															type="number"
															placeholder="กรอกค่าที่ต้องการ"
															{...field}
															onChange={(e) =>
																field.onChange(
																	e.target.value
																		? Number(e.target.value)
																		: undefined,
																)
															}
															value={field.value ?? ''}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									)}

								{currentCondition &&
									currentCondition.type === MessageConditionType.MIN_MAX && (
										<div className="grid grid-cols-2 gap-4">
											<FormField
												control={form.control}
												name="minimum"
												render={({ field }) => (
													<FormItem>
														<FormLabel>ค่าต่ำสุด</FormLabel>
														<FormControl>
															<Input
																type="number"
																placeholder="ค่าต่ำสุด"
																{...field}
																onChange={(e) =>
																	field.onChange(
																		e.target.value
																			? Number(e.target.value)
																			: undefined,
																	)
																}
																value={field.value ?? ''}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
											<FormField
												control={form.control}
												name="maximum"
												render={({ field }) => (
													<FormItem>
														<FormLabel>ค่าสูงสุด</FormLabel>
														<FormControl>
															<Input
																type="number"
																placeholder="ค่าสูงสุด"
																{...field}
																onChange={(e) =>
																	field.onChange(
																		e.target.value
																			? Number(e.target.value)
																			: undefined,
																	)
																}
																value={field.value ?? ''}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									)}

								{/* Channels */}
								<FormField
									control={form.control}
									name="channels"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<ChannelSelector
													value={field.value}
													onChange={field.onChange}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								{/* Channel settings */}
								{selectedChannels.includes(MessageChannel.SMS) && (
									<SMSSettingsForm form={form} />
								)}

								{selectedChannels.includes(MessageChannel.LINE) && (
									<LINESettingsForm />
								)}

								{/* Flex Message Selector for LINE_HOT_GAME */}
								{selectedChannels.includes(MessageChannel.LINE_HOT_GAME) && (
									<FormField
										control={form.control}
										name="hotGameFlexMessageId"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Flex Message</FormLabel>
												<FormControl>
													<FlexMessageSelector
														value={field.value}
														onChange={field.onChange}
														disabled={createAutoMessageMutation.isPending}
													/>
												</FormControl>
												<FormDescription>
													เลือก Flex Message ที่ต้องการส่ง
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}

								{/* Message - optional when using LINE_HOT_GAME */}
								{!selectedChannels.includes(MessageChannel.LINE_HOT_GAME) && (
									<FormField
										control={form.control}
										name="message"
										render={({ field }) => (
											<FormItem>
												<FormLabel>ข้อความ</FormLabel>
												<FormControl>
													<Textarea
														placeholder="กรอกข้อความที่ต้องการส่ง"
														className="min-h-[120px]"
														{...field}
													/>
												</FormControl>
												<FormDescription>
													{selectedChannels.includes(MessageChannel.SMS) && (
														<div className="space-y-1">
															{creditData ? (
																<>
																	<div className="flex items-center justify-between text-sm">
																		<span>
																			{creditData.totalMessage -
																				creditData.messageLeft}{' '}
																			/ {creditData.totalMessage} ตัวอักษร (
																			{getLanguageLabel(creditData.lang)})
																		</span>
																		<span className="font-medium">
																			{creditData.credit} เครดิตต่อข้อความ
																		</span>
																	</div>
																</>
															) : (
																<span className="text-sm">
																	กรอกข้อความเพื่อดูจำนวนเครดิต SMS
																</span>
															)}
														</div>
													)}
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}

								{selectedChannels.includes(MessageChannel.LINE_HOT_GAME) && (
									<FormField
										control={form.control}
										name="message"
										render={({ field }) => (
											<FormItem>
												<FormLabel>ข้อความเพิ่มเติม (ถ้ามี)</FormLabel>
												<FormControl>
													<Textarea
														placeholder="กรอกข้อความเพิ่มเติมที่ต้องการส่งพร้อมกับ Hot Games (ไม่บังคับ)"
														className="min-h-[120px]"
														{...field}
													/>
												</FormControl>
												<FormDescription>
													ข้อความนี้จะแสดงพร้อมกับ Hot Games (ถ้ากรอก)
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}

								{/* Submit buttons */}
								<div className="flex gap-3">
									<Button
										type="button"
										variant="outline"
										onClick={() => navigate('/operator/messaging/auto')}
									>
										ยกเลิก
									</Button>
									<Button
										type="submit"
										disabled={
											createAutoMessageMutation.isPending ||
											selectedChannels.length === 0
										}
									>
										{createAutoMessageMutation.isPending
											? 'กำลังสร้าง...'
											: 'สร้างข้อความอัตโนมัติ'}
									</Button>
								</div>
							</form>
						</Form>
					</CardContent>
				</Card>
			</div>
		</PageContainer>
	)
}

const GuardedCreateAutoMessagePage = withUserPermission(CreateAutoMessagePage, {
	permissionType: PermissionType.Write,
	permissionKey: [PermissionKeys.MANAGE_SMS],
})

export { GuardedCreateAutoMessagePage as CreateAutoMessagePage }
